import styled, { css, keyframes } from "styled-components";
import tw from "twin.macro";

interface StyledCompleteCheckboxProps {
  $primary?: boolean;
  $completed?: boolean;
  $disabled?: boolean;
  $animation?: boolean;
  $checkMarkClassName?: boolean;
}

const burstScale = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const burstAnimation = keyframes`
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: 8;
  }
`;

export const StyledCompleteCheckbox = styled.button<StyledCompleteCheckboxProps>`
  ${tw`tw-w-6 tw-h-6 tw-rounded-full tw-border tw-border-solid tw-relative tw-cursor-pointer`}
  border-color: var(--color-theme-600);
  transition: ease 0.3s all;
  background-color: transparent;
  padding: 0;

  svg {
    ${tw`tw-absolute tw-pointer-events-none`}
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  svg path {
    fill: none;
    stroke: #32b370;
    stroke-width: 3;
    transform: scale(0);
  }

  ${(props) =>
    props.$primary &&
    css`
      svg path {
        stroke: var(--color-primary);
      }
    `}

  svg polyline {
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: 0.3s;
    stroke-dasharray: 13;
    stroke-dashoffset: 13;
    stroke: #d2d2d2;
    ${(props) =>
      props.$primary &&
      css`
        stroke: var(--color-theme-500);
      `}
  }

  ${(props) =>
    props.$completed &&
    css`
      box-shadow: inset 0 0 0 12px var(--color-primary);
      border-color: var(--color-primary);
      border-width: 0px;

      ${!props.$primary &&
      css`
        ${tw`tw-border tw-border-solid`}
        box-shadow: inset 0 0 0 12px #84cc7c;
        border-color: #84cc7c;
      `}

      &:hover {
        box-shadow: inset 0 0 0 12px #84cc7c;
        ${props.$primary &&
        css`
          box-shadow: inset 0 0 0 12px var(--color-primary);
        `}
      }

      svg polyline {
        stroke-dashoffset: 0;
        stroke: #fff;
        ${props.$primary &&
        css`
          stroke: var(--color-theme-100);
        `}
      }

      svg path {
        stroke-dasharray: 8;
        stroke-dashoffset: 0;
        transform-origin: 24px 24px;
      }
    `}

  ${(props) =>
    !props.$completed &&
    !props.$disabled &&
    css`
      &:hover,
      &:focus-visible {
        border: 1px solid var(--color-primary);
        box-shadow: inset 0 0 0 2px var(--color-primary);
        border-width: 0px;
        outline: none;
        ${!props.$primary &&
        css`
          border: 1px solid #84cc7c;
          box-shadow: inset 0 0 0 2px #84cc7c;
        `}
        svg polyline {
          stroke-dashoffset: 0;
        }
      }
    `}

  ${(props) =>
    props.$animation &&
    css`
      /* Delay of 0.01s is for safari transform bug. */
      svg path {
        animation: ${burstScale} 0.6s ease 0s 1 normal,
          ${burstAnimation} 0.4s ease 0.2s 1 normal;
      }
    `}

  ${(props) =>
    props.$disabled &&
    css`
      ${tw`tw-cursor-default`}
      opacity: 0.5;
    `}

  ${(props) =>
    !props.$checkMarkClassName &&
    css`
      svg {
        ${tw`tw-w-12 tw-h-12`}
      }
    `}
`;

StyledCompleteCheckbox.displayName = "StyledCompleteCheckbox";
